'use client';

import Link from 'next/link';
import { Home, Phone } from 'lucide-react';
import { useContactInfo } from '@/context/SiteConfigContext';
import { volumeTierFor, nextVolumeTier, VOLUME_TIERS_SUMMARY, VOLUME_TIERS_TERMS } from '@/lib/pricing/volumeTiers';

/**
 * Multi-window discount call-out.
 *
 * Since 2026-10-25 checkout applies the owner-approved "Window Volume" tiers
 * automatically (3% on 3–5 windows, 7% on 6–10, 12% on 11+; DB row in
 * vendor_discounts, display copy in lib/pricing/volumeTiers.ts). The compact
 * cart variant shows the tier the cart is on and how many more windows reach
 * the next one. Larger projects can still ask for a quote.
 */
export default function WholeHouseDiscountBanner({
  variant = 'full',
  className = '',
  windowCount = 0,
}: {
  /** Compact variant: blinds in the cart that count toward the tiers (not sale-priced). */
  windowCount?: number;
  /** 'full' = standalone section; 'compact' = inline strip for the cart. */
  variant?: 'full' | 'compact';
  className?: string;
}) {
  const contactInfo = useContactInfo();
  // useContactInfo already builds a correct tel: href — don't re-derive it.
  const tel = contactInfo?.phoneTel || '';

  if (variant === 'compact') {
    const tier = volumeTierFor(windowCount);
    const next = nextVolumeTier(windowCount);
    return (
      <div className={`rounded-lg border border-sb-gold/40 bg-sb-gold/10 px-4 py-3 ${className}`}>
        <p className="text-sm font-semibold text-heading-charcoal">
          {tier
            ? `Multi-window discount: ${tier.percent}% off applied for ${windowCount} windows`
            : 'Multi-window discount'}
        </p>
        <p className="text-xs text-gray-700 mt-0.5">
          {next
            ? `Add ${next.more} more window${next.more === 1 ? '' : 's'} to save ${next.tier.percent}%.`
            : 'You have our best multi-window rate.'}
          {' '}
          <span className="text-gray-600">{VOLUME_TIERS_SUMMARY}. {VOLUME_TIERS_TERMS}.</span>
          {tel && (
            <>
              {' '}{windowCount >= 11 ? 'Ordering 20+ windows? Call' : 'Larger project?'}{' '}
              <a href={tel} data-track="cart-volume-banner" className="font-semibold text-primary-red underline">
                {contactInfo.phone}
              </a>
              {windowCount >= 11 ? ' for our best pricing.' : ''}
            </>
          )}
        </p>
      </div>
    );
  }

  return (
    <section className={`bg-sb-bg-alt border-y border-sb-border ${className}`}>
      <div className="container mx-auto px-4 py-5 sm:py-6">
        <div className="flex flex-col md:flex-row md:items-center gap-5 md:gap-8">
          <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-primary-red flex items-center justify-center">
            <Home className="w-6 h-6 text-white" aria-hidden="true" />
          </div>

          <div className="flex-1">
            <h2 className="text-xl sm:text-2xl font-bold text-heading-charcoal">
              Outfitting the whole house?
            </h2>
            <p className="text-sm sm:text-base text-gray-700 mt-1 max-w-2xl">
              {VOLUME_TIERS_SUMMARY}, applied automatically in your cart. For larger
              projects, send us your window count and sizes for a custom quote.
              <span className="block text-xs text-gray-500 mt-1">{VOLUME_TIERS_TERMS}.</span>
            </p>
          </div>

          <div className="flex flex-col sm:flex-row md:flex-col gap-2 md:w-56 flex-shrink-0">
            <Link
              href="/contact"
              className="inline-flex items-center justify-center gap-2 bg-primary-red hover:bg-primary-dark text-white font-semibold py-3 px-5 rounded-lg transition-colors"
            >
              Get a volume quote
            </Link>
            {tel && (
              <a
                href={tel}
                data-track="products-volume-banner"
                className="inline-flex items-center justify-center gap-2 border border-sb-border hover:border-heading-charcoal text-heading-charcoal font-semibold py-3 px-5 rounded-lg transition-colors"
              >
                <Phone className="w-4 h-4" aria-hidden="true" />
                {contactInfo.phone}
              </a>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
